// API client for mstream endpoints

import type { RecordsSeriesJson } from "@/api/recordsSeries";
import type { WindowSeriesJson, WindowView } from "@/api/windowsSeries";
import { tokenStore } from "@/auth/tokenStore";
import {
  type ReportOptions,
  reportPath,
} from "@/pages/PipelineDetail/processingReport";
import type {
  AssignTokenRequest,
  AuditEntry,
  AuditQuery,
  Checkpoint,
  ClaimEntitlementResponse,
  CreateJobRequest,
  CreateServiceRequest,
  CreateTokenRequest,
  DropOutcome,
  DropRequest,
  Entitlements,
  HealthStatus,
  JobMetadata,
  JobMetrics,
  JobWorkerMetrics,
  LogEntry,
  LogsQuery,
  LogsResponse,
  LogsStreamQuery,
  Message,
  PlaygroundTestRequest,
  PlaygroundTestResponse,
  PlaygroundValidateRequest,
  PlaygroundValidateResponse,
  PositionView,
  ReservationStatus,
  ResetOutcome,
  SchemaConvertRequest,
  SchemaConvertResponse,
  SchemaEntry,
  SchemaEntrySummary,
  SchemaFillRequest,
  SchemaVariant,
  Service,
  ServiceResourcesResponse,
  ServicesListQuery,
  SystemStats,
  TestGenerateRequest,
  TestGenerateResponse,
  TestRunRequest,
  TestRunResponse,
  TestSuite,
  TestSuiteSummary,
  TokenCreated,
  TokenSummary,
  UdfScript,
  UserAccessResponse,
  Workspace,
  WorkspaceSummary,
} from "@/types/api";
import { type LogStream, type LogStreamOptions, streamLogs } from "./logStream";

const API_BASE =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.["3T_BRIDGE_MSTREAM_API_BASE"]) ||
  "/api";

function authHeaders(forceAccessToken = false): Record<string, string> {
  // Most endpoints prefer the id token; the per-user entitlement endpoints
  // require the access token specifically (the LM validates that token).
  const token = forceAccessToken
    ? tokenStore.getAccessToken()
    : (tokenStore.getIdToken() ?? tokenStore.getAccessToken());
  return token ? { Authorization: `Bearer ${token}` } : {};
}

class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text().catch(() => "Unknown error");
    // Try to parse JSON error response to extract message
    let message = errorText;
    try {
      const parsed = JSON.parse(errorText);
      if (parsed.message) {
        message = parsed.message;
      }
    } catch {
      // Keep raw text if not JSON
    }
    throw new ApiError(response.status, response.statusText, message);
  }
  return response.json();
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    signal,
  });
  return handleResponse<T>(response);
}

// A generated document (CSV, plain text) rather than JSON — the same
// bearer header and error handling, the body returned as it came.
async function getText(path: string, signal?: AbortSignal): Promise<string> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "GET",
    headers: authHeaders(),
    signal,
  });
  if (!response.ok) await handleResponse<never>(response);
  return response.text();
}

async function post<T, R = Message<T>>(
  path: string,
  body?: unknown,
  opts?: { forceAccessToken?: boolean },
): Promise<R> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(opts?.forceAccessToken),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return handleResponse<R>(response);
}

async function put<T, R = Message<T>>(
  path: string,
  body?: unknown,
): Promise<R> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return handleResponse<R>(response);
}

async function del<T = Message>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
    },
  });
  return handleResponse<T>(response);
}

// Jobs API
export const jobsApi = {
  list: () => get<JobMetadata[]>("/jobs"),

  create: (job: CreateJobRequest) => post<JobMetadata>("/jobs", job),

  stop: (name: string) => post<void>(`/jobs/${encodeURIComponent(name)}/stop`),

  restart: (name: string) =>
    post<JobMetadata>(`/jobs/${encodeURIComponent(name)}/restart`),

  // A pipeline holding undelivered records is refused unless the delete says
  // to discard them.
  delete: (
    name: string,
    { discardBacklog }: { discardBacklog?: boolean } = {},
  ) =>
    del(
      `/jobs/${encodeURIComponent(name)}${discardBacklog === true ? "?discard_backlog=true" : ""}`,
    ),

  getCheckpoints: (name: string) =>
    get<Checkpoint[]>(`/jobs/${encodeURIComponent(name)}/checkpoints`),

  getMetrics: (name: string) =>
    get<JobMetrics>(`/jobs/${encodeURIComponent(name)}/metrics`),

  getWorkers: (name: string) =>
    get<JobWorkerMetrics>(`/jobs/${encodeURIComponent(name)}/workers`),

  getPosition: (name: string) =>
    get<PositionView>(`/jobs/${encodeURIComponent(name)}/position`),

  // The operation id names this reset: a retry with the same id continues it
  // instead of starting another.
  resetPosition: (
    name: string,
    { opId, discardBacklog }: { opId: string; discardBacklog?: boolean },
  ) => {
    const params = new URLSearchParams({ op_id: opId });
    if (discardBacklog === true) params.set("discard_backlog", "true");
    return del<ResetOutcome>(
      `/jobs/${encodeURIComponent(name)}/position?${params.toString()}`,
    );
  },

  // Future: logs endpoint (not yet implemented in mstream)
  getLogs: (name: string, limit = 100) =>
    get<string[]>(`/jobs/${encodeURIComponent(name)}/logs?limit=${limit}`),
};

// Replication slot reservations
export const reservationsApi = {
  list: () => get<ReservationStatus[]>("/reservations"),

  // 200 once dropped, 202 while another request holds the drop or when its
  // outcome needs reconciling; never retried here, since a drop is destructive.
  drop: (request: DropRequest) =>
    post<never, DropOutcome>("/reservations/drop", request),
};

// Services API
export const servicesApi = {
  list: (query: ServicesListQuery = {}) => {
    const params = new URLSearchParams();
    if (query.provider) params.set("provider", query.provider);
    if (query.kind) params.set("kind", query.kind);
    const qs = params.toString();
    return get<Service[]>(`/services${qs ? `?${qs}` : ""}`);
  },

  get: (name: string) => get<Service>(`/services/${encodeURIComponent(name)}`),

  create: (service: CreateServiceRequest) =>
    post<Service>("/services", service),

  remove: (name: string) => del(`/services/${encodeURIComponent(name)}`),

  activate: (name: string) =>
    put<void>(`/services/${encodeURIComponent(name)}/activate`),

  listResources: (name: string) =>
    get<ServiceResourcesResponse>(
      `/services/${encodeURIComponent(name)}/resources`,
    ),

  getResourceContent: (name: string, resource: string) =>
    get<{ filename: string; content: string }>(
      `/services/${encodeURIComponent(name)}/resources/${encodeURIComponent(resource)}`,
    ),

  updateResourceContent: (name: string, resource: string, content: string) =>
    put(
      `/services/${encodeURIComponent(name)}/resources/${encodeURIComponent(resource)}`,
      { content },
    ),
};

// Tokens API
function tokenPath(serviceName: string, tokenId?: string): string {
  const base = `/services/${encodeURIComponent(serviceName)}/tokens`;
  return tokenId ? `${base}/${encodeURIComponent(tokenId)}` : base;
}

export const tokensApi = {
  list: (serviceName: string): Promise<TokenSummary[]> =>
    get<TokenSummary[]>(tokenPath(serviceName)),

  create: async (
    serviceName: string,
    req: CreateTokenRequest,
  ): Promise<TokenCreated> => {
    const res = await post<TokenCreated>(`${tokenPath(serviceName)}`, req);
    if (!res.item) throw new Error("Token creation returned no item");
    return res.item;
  },

  revoke: (serviceName: string, tokenId: string): Promise<Message> =>
    del<Message>(tokenPath(serviceName, tokenId)),

  assign: (
    serviceName: string,
    tokenId: string,
    req: AssignTokenRequest,
  ): Promise<Message<TokenSummary>> =>
    post<TokenSummary>(`${tokenPath(serviceName, tokenId)}/assign`, req),

  unassign: (serviceName: string, tokenId: string): Promise<Message> =>
    post<void, Message>(`${tokenPath(serviceName, tokenId)}/unassign`),
};

// System API
export const systemApi = {
  health: () => get<HealthStatus>("/health"),

  stats: () => get<SystemStats>("/stats"),
};

// Logs API
export const logsApi = {
  /**
   * Get historical log entries with optional filters
   */
  get: (query: LogsQuery = {}) => {
    const params = new URLSearchParams();
    if (query.job_name) params.set("job_name", query.job_name);
    if (query.level) params.set("level", query.level);
    if (query.limit) params.set("limit", query.limit.toString());
    if (query.since) params.set("since", query.since);
    const qs = params.toString();
    return get<LogsResponse>(`/logs${qs ? `?${qs}` : ""}`);
  },

  /**
   * Subscribe to the live log stream.
   *
   * Read over `fetch` rather than with an `EventSource`: the engine verifies
   * the caller's token, and EventSource cannot send an `Authorization` header,
   * so every stream it opened arrived anonymous and was refused. See
   * `logStream.ts` for what that costs and how the reconnect is replaced.
   */
  stream: (
    query: LogsStreamQuery = {},
    handlers: Omit<LogStreamOptions, "token"> = {},
  ): LogStream => {
    const params = new URLSearchParams();
    if (query.job_name) params.set("job_name", query.job_name);
    if (query.level) params.set("level", query.level);
    if (query.initial !== undefined)
      params.set("initial", query.initial.toString());
    const qs = params.toString();
    return streamLogs(`${API_BASE}/logs/stream${qs ? `?${qs}` : ""}`, {
      token: tokenStore.getIdToken() ?? tokenStore.getAccessToken(),
      ...handlers,
    });
  },

  /**
   * Helper to parse SSE message data into LogEntry
   */
  parseLogEntry: (data: string): LogEntry | null => {
    try {
      return JSON.parse(data) as LogEntry;
    } catch {
      return null;
    }
  },
};

// Playground API
export const playgroundApi = {
  test: (request: PlaygroundTestRequest) =>
    post<PlaygroundTestResponse, PlaygroundTestResponse>(
      "/transform/run",
      request,
    ),

  validate: (request: PlaygroundValidateRequest) =>
    post<PlaygroundValidateResponse, PlaygroundValidateResponse>(
      "/transform/validate",
      request,
    ),

  /** Generate test cases from a sample input */
  testGenerate: (request: TestGenerateRequest) =>
    post<TestGenerateResponse, TestGenerateResponse>(
      "/transform/test/generate",
      request,
    ),

  /** Run test cases against a script */
  testRun: (request: TestRunRequest) =>
    post<TestRunResponse, TestRunResponse>("/transform/test/run", request),
};

// Transform Scripts API
function scriptPath(serviceName: string, filename?: string): string {
  const base = `/transform/scripts/${encodeURIComponent(serviceName)}`;
  return filename ? `${base}/${encodeURIComponent(filename)}` : base;
}

export const scriptsApi = {
  list: (serviceName: string) => get<UdfScript[]>(scriptPath(serviceName)),

  get: (serviceName: string, filename: string) =>
    get<UdfScript>(scriptPath(serviceName, filename)),

  create: (serviceName: string, filename: string, content: string) =>
    post<UdfScript, Message<UdfScript>>(scriptPath(serviceName, filename), {
      content,
    }).then((r) => r.item!),

  update: (serviceName: string, filename: string, content: string) =>
    put<UdfScript, UdfScript>(scriptPath(serviceName, filename), { content }),

  delete: (serviceName: string, filename: string) =>
    del(scriptPath(serviceName, filename)),
};

// Schema API
export const schemaApi = {
  /**
   * Get JSON Schema variants for a service resource
   * @param serviceName - Name of the service (e.g., MongoDB connection)
   * @param resource - Resource name (e.g., collection name)
   * @param sampleSize - Optional number of documents to sample (default: 100)
   */
  getSchema: (serviceName: string, resource: string, sampleSize?: number) => {
    const params = new URLSearchParams({ resource });
    if (sampleSize) params.set("sample_size", sampleSize.toString());
    return get<SchemaVariant[]>(
      `/services/${encodeURIComponent(serviceName)}/schema/introspect?${params}`,
    );
  },

  /**
   * Generate a synthetic document from a JSON Schema
   * @param schema - JSON Schema to fill with fake data
   */
  fill: (schema: SchemaFillRequest["schema"]) =>
    post<Record<string, unknown>, Record<string, unknown>>("/schema/fill", {
      schema,
    }),

  /**
   * Convert schema between JSON Schema and Avro formats
   * @param request - Source schema, target encoding, and optional options
   */
  convert: (request: SchemaConvertRequest) =>
    post<SchemaConvertResponse, SchemaConvertResponse>(
      "/schema/convert",
      request,
    ),
};

// Test Suites API
export const testSuitesApi = {
  list: () => get<TestSuiteSummary[]>("/test-suites"),

  get: (id: string) => get<TestSuite>(`/test-suites/${encodeURIComponent(id)}`),

  save: (suite: Omit<TestSuite, "id"> & { id?: string }) =>
    post<TestSuite, Message<TestSuite>>("/test-suites", suite).then(
      (r) => r.item,
    ),

  delete: (id: string) => del(`/test-suites/${encodeURIComponent(id)}`),
};

// Schemas API
function serviceSchemasPath(serviceName: string, id?: string): string {
  const base = `/services/${encodeURIComponent(serviceName)}/schemas`;
  return id ? `${base}/${encodeURIComponent(id)}` : base;
}

export const schemasApi = {
  list: (serviceName: string) =>
    get<SchemaEntrySummary[]>(serviceSchemasPath(serviceName)),

  get: (serviceName: string, id: string) =>
    get<SchemaEntry>(serviceSchemasPath(serviceName, id)),

  save: (
    serviceName: string,
    entry: Omit<SchemaEntry, "id"> & { id?: string },
  ) =>
    post<SchemaEntry, Message<SchemaEntry>>(
      serviceSchemasPath(serviceName),
      entry,
    ).then((r) => r.item),

  delete: (serviceName: string, id: string) =>
    del(serviceSchemasPath(serviceName, id)),
};

// Workspaces API
export const workspacesApi = {
  list: () => get<WorkspaceSummary[]>("/workspaces"),

  get: (id: string) => get<Workspace>(`/workspaces/${encodeURIComponent(id)}`),

  create: (ws: Partial<Workspace> & { name: string }) =>
    post<Workspace, Message<Workspace>>("/workspaces", ws).then((r) => {
      if (!r.item) throw new Error("Workspace create: response missing item");
      return r.item;
    }),

  update: (id: string, ws: Partial<Workspace> & { name: string }) =>
    put<Workspace, Message<Workspace>>(
      `/workspaces/${encodeURIComponent(id)}`,
      ws,
    ).then((r) => {
      if (!r.item) throw new Error("Workspace update: response missing item");
      return r.item;
    }),

  delete: (id: string) => del(`/workspaces/${encodeURIComponent(id)}`),
};

// Audit Log API
export const auditApi = {
  list: (query: AuditQuery = {}, signal?: AbortSignal) => {
    const params = new URLSearchParams();
    if (query.action) params.set("action", query.action);
    if (query.resource) params.set("resource", query.resource);
    if (query.limit) params.set("limit", query.limit.toString());
    if (query.since) params.set("since", query.since);
    if (query.until) params.set("until", query.until);
    const qs = params.toString();
    return get<AuditEntry[]>(`/audit-log${qs ? `?${qs}` : ""}`, signal);
  },
};

// Reports API — the per-pipeline processing record the engine generates
// from the lineage ledger (TTTL-172). Fetched, not linked: a plain anchor
// cannot carry the bearer token the API requires.
/** The window a series is asked for. */
interface SeriesWindow {
  from?: string;
  to?: string;
  points?: number;
}

/** A series route's query string: the window, and any fixed parameters
 *  first; empty when there is nothing to ask. */
function windowQuery(
  window: SeriesWindow,
  fixed: Record<string, string> = {},
): string {
  const params = new URLSearchParams(fixed);
  if (window.from) params.set("from", window.from);
  if (window.to) params.set("to", window.to);
  if (window.points) params.set("points", String(window.points));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export const reportsApi = {
  pipeline: (pipeline: string, options: ReportOptions) =>
    getText(reportPath(pipeline, options)),
  // Records watched, dropped and incomplete at `points` evenly spaced
  // moments over (from, to] (TTTL-257) — one request for the whole line.
  recordsSeries: (
    pipeline: string,
    window: SeriesWindow,
    signal?: AbortSignal,
  ) =>
    get<RecordsSeriesJson>(
      `/reports/pipelines/${encodeURIComponent(pipeline)}/series/records${windowQuery(window)}`,
      signal,
    ),
  // One view of a windowed pipeline's store at `points` evenly spaced
  // moments: the newest sample at each, never a sum.
  windowsSeries: (
    pipeline: string,
    view: WindowView,
    window: SeriesWindow,
    signal?: AbortSignal,
  ) =>
    get<WindowSeriesJson>(
      `/reports/pipelines/${encodeURIComponent(pipeline)}/series/windows${windowQuery(window, { view })}`,
      signal,
    ),
};

// Entitlements API — license snapshot from bridge-engine.
export const entitlementsApi = {
  get: (signal?: AbortSignal) => get<Entitlements>("/entitlements", signal),

  // Per-user access (TTTL-40). No body; the access token (not the id token) is
  // forwarded to the License Manager, so force it here.
  userAccess: () =>
    post<void, UserAccessResponse>("/entitlements/user-access", undefined, {
      forceAccessToken: true,
    }),

  claimEntitlement: () =>
    post<void, ClaimEntitlementResponse>(
      "/entitlements/claim-user-entitlement",
      undefined,
      { forceAccessToken: true },
    ),
};

// Export error class for handling
export { ApiError };

// Convenience export of all APIs
export const api = {
  jobs: jobsApi,
  services: servicesApi,
  tokens: tokensApi,
  system: systemApi,
  playground: playgroundApi,
  scripts: scriptsApi,
  logs: logsApi,
  schema: schemaApi,
  testSuites: testSuitesApi,
  schemas: schemasApi,
  workspaces: workspacesApi,
  audit: auditApi,
  reports: reportsApi,
  entitlements: entitlementsApi,
};

export default api;
